import { useState } from "react";
import { useAuth, roleLabel } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Save, KeyRound } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [photoUrl, setPhotoUrl] = useState(user?.photo_url || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", { name, phone, photo_url: photoUrl });
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingPw(true);
    try {
      await api.patch("/auth/me", { current_password: currentPw, new_password: newPw });
      toast.success("Password changed");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingPw(false); }
  };

  const uploadPhoto = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data?.url) setPhotoUrl(r.data.url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8 max-w-2xl">
      <div className="mb-8">
        <div className="label-caps text-muted-foreground mb-2">Account</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Your profile</h1>
        <p className="text-sm text-muted-foreground mt-2">Update how you appear across Sparkle and change your password.</p>
      </div>

      <div className="border border-border bg-card rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="w-16 h-16 border-2 border-border">
            <AvatarImage src={photoUrl} alt={name} />
            <AvatarFallback>{(name || "U")[0]}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-display font-semibold text-lg">{name || user?.email}</div>
            <div className="text-xs text-muted-foreground">{roleLabel(user?.role)} · {user?.email}</div>
            <label className="inline-block mt-2 text-xs font-medium text-primary hover:underline cursor-pointer" data-testid="profile-upload-photo">
              Change photo
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
              />
            </label>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="profile-name">Full name</Label>
            <Input id="profile-name" data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="profile-phone">Phone</Label>
            <Input id="profile-phone" data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled />
            <p className="text-[0.68rem] text-muted-foreground mt-1">Your email can't be changed here — ask an administrator.</p>
          </div>
          <Button onClick={saveProfile} disabled={savingProfile} className="rounded-xl" data-testid="profile-save">
            <Save className="w-4 h-4 mr-1" />{savingProfile ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-4 h-4 text-primary" />
          <h2 className="font-display font-semibold text-lg">Change password</h2>
        </div>
        <div className="space-y-4">
          <div>
            <Label htmlFor="pw-current">Current password</Label>
            <Input id="pw-current" data-testid="pw-current" type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="pw-new">New password</Label>
            <Input id="pw-new" data-testid="pw-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="pw-confirm">Confirm new password</Label>
            <Input id="pw-confirm" data-testid="pw-confirm" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
          </div>
          <Button onClick={changePassword} disabled={savingPw || !currentPw || !newPw} className="rounded-xl" data-testid="pw-save">
            {savingPw ? "Updating…" : "Update password"}
          </Button>
        </div>
      </div>
    </div>
  );
}
